import React, { useMemo, useState, useEffect } from "react"
import { Button, UncontrolledAlert } from "reactstrap"
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import TableContainer from "../../../components/Common/DataTableContainer"
import { fetchProducts, deleteProduct } from "../../../redux/actions/pvs"
import ActionDropdown from "components/ActionDropdown"
import { toast } from "react-toastify"
import Breadcrumbs from "../../../components/Common/Breadcrumb"

const ProductList = () => {
  document.title = "Product List | Admin Dashboard"

  const history = useNavigate()
  const dispatch = useDispatch()
  const pvsState = useSelector(state => state.pvs)
  const fetchProductsState = useSelector(
    state => state.screens.pvs.fetchProducts
  )
  const deleteProductState = useSelector(
    state => state.screens.pvs.deleteProduct
  )
  const token = useSelector(state => state.auth.token)

  const [productData, setProductData] = useState([])
  const [alert, setAlert] = useState({
    visible: false,
    message: "",
    color: "success",
  })

  useEffect(() => {
    dispatch(fetchProducts())
  }, [])

  const columns = useMemo(
    () => [
      {
        header: "Product Name",
        accessorKey: "product_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Category",
        accessorKey: "category_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Subcategory",
        accessorKey: "subcategory_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original
          return (
            <div>
              <button
                className="btn btn-sm btn-info me-2"
                onClick={() => history(`/edit-product`, {state: {product: row}})}
                title="Edit Product"
              >
                <FaEdit /> Edit
              </button>
            </div>
          )
        },
      },
    ],
    [history]
  )

  const handleDelete = (enc_product_id, product_name) => {
    if (window.confirm(`Are you sure you want to delete "${product_name}"?`)) {
      dispatch(deleteProduct(enc_product_id))
    }
  }

  useEffect(() => {
    if (pvsState && Array.isArray(pvsState.productList)) {
      setProductData(pvsState.productList)
    }
  }, [pvsState])

  useEffect(() => {
    if (deleteProductState.success) {
      setAlert({
        visible: true,
        message: "Product deleted successfully!",
        color: "success",
      })
      toast.success("Product deleted successfully!")
      dispatch(fetchProducts()) // Refresh the list
    } else if (deleteProductState.error) {
      setAlert({
        visible: true,
        message: "Error deleting product",
        color: "danger",
      })
      toast.error("Error deleting product")
    }
  }, [deleteProductState, dispatch])

  // Auto-hide alert after 3 seconds
  useEffect(() => {
    if (alert.visible) {
      const timer = setTimeout(() => {
        setAlert(prev => Object.assign({}, prev, { visible: false }))
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [alert.visible])

  return (
    <div className="page-content">
      <div className="container-fluid">

        <div className="d-flex align-items-center justify-content-between mt-4 mb-3">
          <div className="flex-grow-1">
            <h4 className="h4 nom-title mb-0">Product List</h4>
          </div>
          <div style={{ position: "relative" }}>
            <Button color="primary" onClick={() => history("/add-product")}>
              <FaPlus /> Add Product
            </Button>

            {/* Overlapping alert on button */}
            {alert.visible && (
              <UncontrolledAlert
                color={alert.color}
                style={{
                  position: "absolute",
                  top: "-5px",
                  right: "-5px",
                  padding: "0.75rem 0.75rem",
                  minWidth: "400px",
                  zIndex: 9999,
                  fontSize: "1rem",
                  textAlign: "center",
                }}
              >
                {alert.message}
              </UncontrolledAlert>
            )}
          </div>
        </div>

        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={productData || []}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search products..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4"
          noDataMessage={fetchProductsState.message || "No products found"}
        />
      </div>
    </div>
  )
}

export default ProductList
